import { getPfFiles, postPfFile } from '@client-shared/api/pf-files.api.js'

import constants from '@/config/constants.js'

/**
 * One-time migration plugin for legacy clipboard files (pre pf-files migration).
 * Legacy files have s3Key but no pfFileId. Can be removed once all users have migrated.
 */
export default {
  install: (app) => {
    const $router = app.config.globalProperties.$router
    const $auth = app.config.globalProperties.$auth
    const $axios = app.config.globalProperties.$axios

    const removeGuard = $router.beforeEach(async () => {
      if (!$auth.loggedIn) {
        return
      }

      removeGuard()

      const keyPrefix = `${constants.LOCAL_STORAGE_PREFIX}-${$auth.user._id}-`
      const lsKeys = Object.keys(localStorage)
        .filter(key => key.startsWith(keyPrefix))

      for (const lsKey of lsKeys) {
        const projectId = lsKey.slice(keyPrefix.length)
        const rawContent = JSON.parse(localStorage.getItem(lsKey) || '{}')
        const rawFiles = rawContent.clipboard?.files || []
        const isLegacyFile = f => !f.pfFileId && f.s3Key && f.isUploadCompleted && !f.assignedAs
        const filesToMigrate = rawFiles.filter(isLegacyFile)
        const filesMigrated = rawFiles.filter(f => !isLegacyFile(f))

        if (filesToMigrate.length === 0) {
          continue
        }

        let existingPfFiles

        for (const legacyFile of filesToMigrate) {
          try {
            const pfFile = await postPfFile({
              axios: $axios,
              projectId,
              s3Key: legacyFile.s3Key,
              fileName: decodeURIComponent(legacyFile.name),
              mimeType: legacyFile.mimeType,
              size: legacyFile.size,
              isClipboardFile: true,
            })

            filesMigrated.push({
              ...legacyFile,
              pfFileId: pfFile._id,
            })
          } catch (err) {
            if (err.response?.data?.error === 'S3_KEY_ALREADY_IN_USE') {
              // The PfFile was created in a prior migration attempt but localStorage
              // was not updated. Look up the existing PfFile to complete the migration.
              try {
                if (!existingPfFiles) {
                  existingPfFiles = await getPfFiles({
                    axios: $axios,
                    projectId,
                  })
                }

                const decodedName = decodeURIComponent(legacyFile.name)
                const existingPfFile = existingPfFiles.find(f => f.s3Key === legacyFile.s3Key && f.fileName === decodedName)

                if (existingPfFile) {
                  filesMigrated.push({
                    ...legacyFile,
                    pfFileId: existingPfFile._id,
                  })

                  continue
                }
              } catch {
                // Fall through to the legacy push below
              }
            }

            console.error('Failed to migrate legacy clipboard file:', err)
            filesMigrated.push(legacyFile) // If the migration failed the clipboard file would be lost
          }
        }

        rawContent.clipboard.files = filesMigrated

        localStorage.setItem(lsKey, JSON.stringify(rawContent))
      }
    })
  },
}
